import * as Util from "../common/util";
import * as Data from "../interfaces/data";
import { ITranslatedText } from "../interfaces/translations";
import { ICustomerDocument } from "./documentLibraryData";
import { IReportDefinition } from "./reportData";
import { ITemplateAccessRoleTemplate } from "./templateAccessRoleData";

// forms that can be added to a list
export interface ITemplateListItem {
    TemplateFormID: number;
}

export interface ITemplateLinkedCustomerDocument {
    TemplateCustomerDocumentID: number;
    CustomerDocumentID: number;
    StorageKey: string;
    IsActive: boolean;
    Description: string;
    FileName: string;
}

// a single list item
export interface ITemplateList {
    ID: number;
    ListName: string;
    HasForm: boolean;
    HasAttributes: boolean;
    SortOrder?: number;
    IsActive: boolean;

    /// lists that are children of this list.  These are lists that get
    /// created automatically when an appointment is created from
    /// this template.
    Children: ITemplateList[];

    /// these are forms that can be added to this list.
    ListItems: ITemplateListItem[];

    /// if HasForm is true, this will contain the ID of the
    /// form associated with the list item.
    FormID?: number;

    /// if HasAttributes is true, this will contain the ID of the
    /// form to use for the attributes.
    AttributeFormID?: number;
}

export interface ITemplateNavigation {
    ID: number;
    TemplateFormID?: number;
    NavigationTypeID: number;
    NavigationName: string;
    NavigationNameTranslations: ITranslatedText[];
    NavigationIcon: string;
    SortOrder?: number;
    IsActive: boolean;
    ListItems: ITemplateListItem[];
    Lists: ITemplateList[];
}

export interface ITemplate {
    /// unique ID for the template
    ID: number;

    /// the name of the template
    TemplateName: string;

    /// reference to the kind of targets associated with this template
    TargetTemplateID: number;

    IsActive: boolean;

    NotifyUserOnAssignment: boolean;

    Navigations: ITemplateNavigation[];

    LinkedCustomerDocuments: ITemplateLinkedCustomerDocument[];
    TemplateRoles: ITemplateAccessRoleTemplate[];

    ReportID?: number;

    TemplateSettings: ITemplateSetting[];
}

export interface ITemplateMasterForm {
    /// unique ID for the Form Template
    ID: any;

    /// the name of the form
    FormName: string;

    /// additional description of the form
    FormDescription: string;
}

export interface ITemplateMasterTarget {
    ID: number;
    TemplateName: string;
    URLFriendlyName: string;
    TemplateTypeName: string;
}

export interface ITemplateSettingType {
    ID: number;
    SettingType: string;
    SettingGroup: string;
    SettingKey: string;
    IsSingleInstance: boolean;
    DefaultValue: string;
}

export interface ITemplateSetting {
    // From TemplateSetting table
    ID: number;
    TemplateSettingTypeID: number;
    Value: string;
    IsActive: boolean;

    // From TemplateSettingType table
    SettingType?: string;
    SettingGroup?: string;
    SettingKey?: string;
    IsSingleInstance?: boolean;
}

export interface ITemplateMaster {
    /// the definition of the template
    Template: ITemplate;

    /// if true, then the user is allowed to change the target template type.
    /// if appointments exist for this template, then the target cannot be changed.
    CanChangeTargetTemplate: boolean;

    /// all available types of navigation items
    NavigationTypes: Array<Data.ICodeModel<number>>;

    /// all forms that can be selected for a navigation item
    NavigationForms: ITemplateMasterForm[];

    /// all forms that can be selected as an additional form
    AdditionalForms: ITemplateMasterForm[];

    /// all forms that can be selected as an attribute form
    AttributeForms: ITemplateMasterForm[];

    /// all possible target templates that can be associated with this template
    /// only used if CanChangeTargetTemplate is true
    TargetTemplates: ITemplateMasterTarget[];

    /// the name of the selected target template
    /// only used if CanChangeTargetTemplate is false
    TargetTemplateName: string;

    // all possible customer documents that can be linked to this template
    AllCustomerDocuments: ICustomerDocument[];

    AllReports: IReportDefinition[];

    // All setting types
    TemplateSettingTypes: ITemplateSettingType[];

    // Settings for this template
    TemplateSettings: ITemplateSetting[];
}

export function GetMaster(id: number): JQueryDeferred<Data.IResponse<ITemplateMaster>> {
    return Util.callWebService(
        "/api/Template/" + id,
        "GET");
}

export function Save(template: ITemplate): JQueryDeferred<Data.IResponse<ITemplateMaster>> {
    return Util.callWebService(
        "/api/Template/Save",
        "POST",
        template);
}

/// used to change the IsActive status of a Template object
export interface ITemplateSetIsActiveRequest {
    /// the ID of the Template to change.
    TemplateID: number;

    /// the new value for the IsActive status.
    IsActive: boolean;
}

export function SetIsActive(request: ITemplateSetIsActiveRequest): JQueryDeferred<Data.IResponse<string>> {
    return Util.callWebService(
        "/api/Template/SetIsActive",
        "POST",
        request);
}

export function CopyTemplate(ID: any): JQueryDeferred<Data.IResponse<number>> {
    return Util.callWebService(
        "/api/Template/CopyTemplate", "POST",
        {
            ID,
        });
}

// constants
export const NAVIGATIONTYPE_FORM: number = 1;
export const NAVIGATIONTYPE_LIST: number = 2;
export const NAVIGATIONTYPE_REPORT: number = 3;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/data/templateData.ts